import type {
  AnswerSource,
  BotContent,
  CardContent,
  ChatMessage,
  Choice,
  ChoicesContent,
  ConfirmContent,
  Field,
  ReceiptContent,
  Section,
  StatusContent,
  StepState,
  TableContent,
  Tone,
  WidgetContent,
} from '@oneapp/shared';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useInRouterContext } from 'react-router';
import { CopyButton } from '../components/CopyButton';
import { Icon, iconFor } from '../components/icons';
import { Badge, Button, Callout } from '../components/ui';
import { Markdown, safeUrl } from './markdown';
import { CopyRow } from './PaymentSheet';

export interface BotHandlers {
  choose(message: ChatMessage, choice: Choice): void;
  openWidget(message: ChatMessage): void;
}

interface Props {
  message: ChatMessage;
  /** Still waiting for an answer (options can be tapped). */
  open: boolean;
  selected?: string | undefined;
  handlers: BotHandlers;
}

const BADGE_TONE = {
  neutral: 'gray',
  info: 'navy',
  success: 'green',
  warning: 'amber',
  danger: 'red',
} as const satisfies Record<Tone, string>;

/** One bot message. Types this app doesn't know yet are skipped, as the message contract allows. */
export function BotMessage({ message, open, selected, handlers }: Props) {
  const content = message.content as BotContent;
  const pick = (choice: Choice) => handlers.choose(message, choice);
  switch (content.type) {
    case 'text':
      return <TextBubble content={content} />;
    case 'choices':
      return <ChoicesView content={content} open={open} onPick={pick} />;
    case 'card':
      return <CardView content={content} open={open} onPick={pick} />;
    case 'table':
      return <TableView content={content} />;
    case 'confirm':
      return <ConfirmView content={content} open={open} selected={selected} onPick={pick} />;
    case 'receipt':
      return <ReceiptView content={content} />;
    case 'status':
      return <StatusView content={content} />;
    case 'widget':
      // One of the app's pages shows live data, so it can be opened again from an older message.
      return (
        <WidgetCard
          content={content}
          open={open || content.widget.kind === 'page'}
          onOpen={() => handlers.openWidget(message)}
        />
      );
    default:
      return null;
  }
}

function TextBubble({ content }: { content: Extract<BotContent, { type: 'text' }> }) {
  return (
    <div className={`bubble${content.tone && content.tone !== 'neutral' ? ` bubble--${content.tone}` : ''}`}>
      {content.title ? <span className="bubble__title">{content.title}</span> : null}
      <Markdown text={content.text} />
      <SourceLine source={content.source} />
    </div>
  );
}

function SourceLine({ source }: { source?: AnswerSource }) {
  if (!source) return null;
  const url = source.url && safeUrl(source.url);
  return (
    <small className="answer-source">
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {source.label}
        </a>
      ) : (
        source.label
      )}
    </small>
  );
}

function ChoiceIcon({ choice, size = 21 }: { choice: Choice; size?: number }) {
  const name = iconFor(choice.icon);
  return name ? <Icon name={name} size={size} /> : null;
}

function ChoicesView({
  content,
  open,
  onPick,
}: {
  content: ChoicesContent;
  open: boolean;
  onPick: (choice: Choice) => void;
}) {
  const { t } = useTranslation();
  const questionId = useId();
  const layout = content.layout ?? 'chips';
  const group = content.text ? { 'aria-labelledby': questionId } : { 'aria-label': t('chat.choices') };
  return (
    <>
      {content.text ? (
        <div className="bubble" id={questionId}>
          <Markdown text={content.text} />
        </div>
      ) : null}
      {open ? (
        <fieldset className="options" {...group}>
          {layout === 'cards' ? (
            <div className="tiles">
              {content.choices.map((choice, i) => {
                const wide = content.choices.length % 2 === 1 && i === content.choices.length - 1;
                const muted = choice.tone === 'neutral';
                return (
                  <button
                    key={choice.id}
                    type="button"
                    className={`tile${wide ? ' tile--wide' : ''}${muted ? ' tile--muted' : ''}`}
                    disabled={choice.disabled}
                    onClick={() => onPick(choice)}
                  >
                    {iconFor(choice.icon) ? (
                      <span className="icon-tile">
                        <ChoiceIcon choice={choice} />
                      </span>
                    ) : null}
                    <span className="tile__text">
                      <span className="tile__label">{choice.label}</span>
                      {choice.description ? <span className="tile__desc">{choice.description}</span> : null}
                    </span>
                    {choice.badge ? (
                      <span className="tile__badge">
                        <Badge tone={BADGE_TONE[choice.tone ?? 'neutral']}>{choice.badge}</Badge>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : layout === 'list' ? (
            <div className="optlist">
              {content.choices.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  className={`optlist__row${choice.tone === 'neutral' ? ' optlist__row--muted' : ''}`}
                  disabled={choice.disabled}
                  onClick={() => onPick(choice)}
                >
                  {iconFor(choice.icon) ? (
                    <span className="icon-tile">
                      <ChoiceIcon choice={choice} size={19} />
                    </span>
                  ) : null}
                  <span className="optlist__text">
                    <span className="optlist__label">{choice.label}</span>
                    {choice.description ? <span className="optlist__desc">{choice.description}</span> : null}
                  </span>
                  {choice.badge ? <Badge tone={BADGE_TONE[choice.tone ?? 'neutral']}>{choice.badge}</Badge> : null}
                  <Icon name="chevron-right" size={18} />
                </button>
              ))}
            </div>
          ) : (
            <div className="chips">
              {content.choices.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  className={`chip${choice.tone === 'info' || choice.tone === 'success' ? ' chip--primary' : ''}`}
                  disabled={choice.disabled}
                  onClick={() => onPick(choice)}
                >
                  <ChoiceIcon choice={choice} size={17} />
                  {choice.label}
                </button>
              ))}
            </div>
          )}
          {content.hint ? <p className="hint">{content.hint}</p> : null}
        </fieldset>
      ) : null}
    </>
  );
}

/** A status in a card (late, due, filed, active) shows as a pill; its colour is never the only clue. */
const FIELD_TONE = {
  success: 'green',
  info: 'navy',
  warning: 'saffron',
  danger: 'red',
} as const satisfies Record<Exclude<Tone, 'neutral'>, string>;

function FieldRow({ field }: { field: Field }) {
  const inRouter = useInRouterContext();
  const pill = field.tone && field.tone !== 'neutral' && !field.emphasis && field.value ? field.tone : null;
  const className = `fields__row${field.emphasis ? ' fields__row--emphasis' : ''}${pill ? ' fields__row--pill' : ''}`;
  // A row with a page of its own (a filing's receipt): its label opens it.
  const path = field.path && /^\/(?!\/)/.test(field.path) ? field.path : null;
  const label = !path ? (
    field.label
  ) : inRouter ? (
    <Link className="fields__link" to={path}>
      {field.label}
    </Link>
  ) : (
    <a className="fields__link" href={path}>
      {field.label}
    </a>
  );
  return (
    <div className={className}>
      <dt>
        {label}
        {field.hint ? <span className="fields__hint">{field.hint}</span> : null}
      </dt>
      <dd>{pill ? <Badge tone={FIELD_TONE[pill]}>{field.value}</Badge> : field.value}</dd>
    </div>
  );
}

function Fields({ fields }: { fields: Field[] }) {
  // Values to copy (a challan's bank details, C5) are rows of their own, each with a copy button.
  const rows = fields.filter((field) => !field.copy);
  const copies = fields.filter((field) => field.copy);
  return (
    <>
      {rows.length ? (
        <dl className="fields">
          {rows.map((field, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows in order; labels can repeat
            <FieldRow key={i} field={field} />
          ))}
        </dl>
      ) : null}
      {copies.length ? (
        <div className="copyrows">
          {copies.map((field) => (
            <CopyRow
              key={field.label}
              label={field.label}
              value={field.value}
              copyLabel={field.copy ?? field.label}
              mono={field.mono}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function Sections({ sections }: { sections: Section[] }) {
  return (
    <>
      {sections.map((section, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: sections in order
        <div key={i} className="card__section">
          {section.title && section.aside ? (
            <div className="card__section-head">
              <h4 className="card__section-title">{section.title}</h4>
              <span className="card__section-aside">
                {section.asideIcon ? <Icon name={iconFor(section.asideIcon) ?? 'lock'} size={13} /> : null}
                {section.aside}
              </span>
            </div>
          ) : section.title ? (
            <h4 className="card__section-title">{section.title}</h4>
          ) : null}
          <Fields fields={section.fields} />
        </div>
      ))}
    </>
  );
}

/** One of the app's own pages from a card (Phase 16: the People page, to invite an accountant). */
function CardLink({ link }: { link: { label: string; path: string } }) {
  const inRouter = useInRouterContext();
  return (
    <div className="card__actions">
      {inRouter ? (
        <Link className="btn btn--secondary btn--small" to={link.path}>
          {link.label}
        </Link>
      ) : (
        <a className="btn btn--secondary btn--small" href={link.path}>
          {link.label}
        </a>
      )}
    </div>
  );
}

/** An official website to do something on (step 2: a challan made on gst.gov.in), opened in a new tab. */
function CardWebLink({ link }: { link: { label: string; url: string } }) {
  return (
    <div className="card__actions">
      <a className="btn btn--small" href={link.url} target="_blank" rel="noopener noreferrer">
        {link.label}
        <Icon name="external-link" size={16} />
      </a>
    </div>
  );
}

function CardView({ content, open, onPick }: { content: CardContent; open: boolean; onPick: (c: Choice) => void }) {
  const titleId = useId();
  const icon = iconFor(content.icon);
  const stacked = content.actionsLayout === 'stack';
  const card = (
    <section className={`card card--chat${content.tone ? ` card--${content.tone}` : ''}`} aria-labelledby={titleId}>
      <div className="card__head">
        {icon ? (
          <span className="icon-tile">
            <Icon name={icon} />
          </span>
        ) : null}
        <div className="card__titles">
          <h3 className="card__title" id={titleId}>
            {content.title}
          </h3>
          {content.subtitle ? <span className="card__subtitle">{content.subtitle}</span> : null}
        </div>
      </div>
      <Sections sections={content.sections} />
      {content.note ? (
        <div className={`card__note card__note--${content.note.tone ?? 'info'}`}>
          {content.note.tone === 'neutral' ? null : (
            <Icon
              name={content.note.tone === 'warning' || content.note.tone === 'danger' ? 'alert-triangle' : 'info'}
              size={16}
            />
          )}
          <span>
            {content.note.title ? <strong>{content.note.title}</strong> : null}
            {content.note.title ? ' — ' : null}
            {content.note.text}
          </span>
        </div>
      ) : null}
      {content.footer ? (
        <div className="card__footer">
          <Markdown text={content.footer} />
        </div>
      ) : null}
      <SourceLine source={content.source} />
      {content.link && /^\/(?!\/)/.test(content.link.path) ? <CardLink link={content.link} /> : null}
      {content.openUrl && /^https:\/\/[^/]/.test(content.openUrl.url) ? <CardWebLink link={content.openUrl} /> : null}
      {open && content.actions?.length ? (
        <div className={`card__actions${stacked ? ' card__actions--stack' : ''}`}>
          {content.actions.map((action, i) => (
            <Button
              key={action.id}
              small={!stacked}
              inline={!stacked}
              variant={i === 0 ? 'primary' : 'secondary'}
              disabled={action.disabled}
              onClick={() => onPick(action)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}
    </section>
  );
  return content.hint ? (
    <>
      {card}
      <p className="card-hint">{content.hint}</p>
    </>
  ) : (
    card
  );
}

function TableView({ content }: { content: TableContent }) {
  const titleId = useId();
  const align = (key: string) => content.columns.find((c) => c.key === key)?.align ?? 'left';
  return (
    <figure className="table-card" aria-labelledby={content.title ? titleId : undefined}>
      {content.title ? (
        <p className="table-card__title" id={titleId}>
          {content.title}
        </p>
      ) : null}
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a wide table scrolls sideways; the keyboard must reach it */}
      <section className="table-card__scroll" tabIndex={0} aria-label={content.title ?? content.caption}>
        <table>
          {content.caption ? <caption>{content.caption}</caption> : null}
          <thead>
            <tr>
              {content.columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={column.align === 'right' ? 'is-num' : undefined}
                  style={{ textAlign: column.align ?? 'left' }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {content.rows.map((row, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rows in order
              <tr key={i}>
                {content.columns.map((column) => (
                  <td
                    key={column.key}
                    className={align(column.key) === 'right' ? 'is-num' : undefined}
                    style={{ textAlign: align(column.key) }}
                  >
                    {row[column.key] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {content.footer ? (
            <tfoot>
              <tr>
                {content.columns.map((column) => (
                  <td
                    key={column.key}
                    className={align(column.key) === 'right' ? 'is-num' : undefined}
                    style={{ textAlign: align(column.key) }}
                  >
                    {content.footer?.[column.key] ?? ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </section>
    </figure>
  );
}

function ConfirmView({
  content,
  open,
  selected,
  onPick,
}: {
  content: ConfirmContent;
  open: boolean;
  selected?: string | undefined;
  onPick: (c: Choice) => void;
}) {
  const titleId = useId();
  // Gap 4b: a declaration to tick before confirming (GSTR-1A's, that GSTR-3B takes its figures).
  const [agreed, setAgreed] = useState(false);
  const options = [content.confirm, ...(content.cancel ? [content.cancel] : []), ...(content.secondary ?? [])];
  const chosen = selected ? options.find((o) => o.id === selected) : undefined;
  return (
    <section className="card card--chat confirm" aria-labelledby={titleId}>
      <div className={`card__head${content.eyebrow ? ' confirm__head' : ''}`}>
        <div className="card__titles">
          {content.eyebrow ? (
            <span className="confirm__eyebrow">
              <Icon name="shield-check" size={14} />
              {content.eyebrow}
            </span>
          ) : null}
          <h3 className="card__title" id={titleId}>
            {content.title}
          </h3>
          {content.subtitle ? <span className="card__subtitle">{content.subtitle}</span> : null}
        </div>
      </div>
      {content.text ? (
        <div className="confirm__text">
          <Markdown text={content.text} />
        </div>
      ) : null}
      <Sections sections={content.sections} />
      {content.warning || open || chosen ? (
        <div className="confirm__foot">
          {content.warning ? (
            <Callout tone="amber" icon="alert-triangle">
              {content.warning}
            </Callout>
          ) : null}
          {open && content.agree ? (
            <label className="confirm__agree">
              <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
              <span>{content.agree}</span>
            </label>
          ) : null}
          {open ? (
            <div className="confirm__actions">
              <Button
                disabled={content.confirm.disabled || (Boolean(content.agree) && !agreed)}
                onClick={() => onPick(content.confirm)}
              >
                {content.confirm.label}
              </Button>
              {content.cancel ? (
                <Button variant="secondary" onClick={() => content.cancel && onPick(content.cancel)}>
                  {content.cancel.label}
                </Button>
              ) : null}
              {(content.secondary ?? []).map((option) => (
                <Button key={option.id} variant="ghost" disabled={option.disabled} onClick={() => onPick(option)}>
                  {option.label}
                </Button>
              ))}
            </div>
          ) : chosen ? (
            <p className="confirm__chosen">
              <Icon name="check" size={16} strokeWidth={2.2} />
              {chosen.label}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ReceiptView({ content }: { content: ReceiptContent }) {
  const { t } = useTranslation();
  const titleId = useId();
  const inRouter = useInRouterContext();
  const download = content.downloadUrl ? safeUrl(content.downloadUrl) : null;
  // Only the app's own pages: a path that starts with one slash.
  const link = content.link && /^\/(?!\/)/.test(content.link.path) ? content.link : null;
  return (
    <section className="card card--chat receipt" aria-labelledby={titleId}>
      <div className="receipt__head">
        <span className="receipt__badge">
          <Icon name="check" size={24} strokeWidth={2.4} />
        </span>
        <span className="receipt__titles">
          <h3 className="card__title" id={titleId}>
            {content.title}
          </h3>
          {content.subtitle ? <span className="card__subtitle">{content.subtitle}</span> : null}
        </span>
      </div>
      <div className="receipt__ref">
        <span className="receipt__ref-label">{content.reference.label}</span>
        <span className="receipt__ref-value">{content.reference.value}</span>
        <CopyButton value={content.reference.value} label={content.reference.label} />
      </div>
      <Sections sections={content.sections} />
      {download || link ? (
        <div className="card__actions receipt__actions">
          {link ? (
            inRouter ? (
              <Link className="btn btn--secondary btn--small" to={link.path}>
                <Icon name="file-text" size={18} />
                {link.label}
              </Link>
            ) : (
              <a className="btn btn--secondary btn--small" href={link.path}>
                <Icon name="file-text" size={18} />
                {link.label}
              </a>
            )
          ) : null}
          {download ? (
            <a className="btn btn--secondary btn--small btn--inline" href={download} download>
              <Icon name="download" size={18} />
              {t('chat.download')}
            </a>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

const STEP_ICON = {
  done: 'check-circle',
  active: 'clock',
  pending: 'circle',
  error: 'x-circle',
} as const satisfies Record<StepState, string>;

function StatusView({ content }: { content: StatusContent }) {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <section className="card card--chat" aria-labelledby={content.title ? titleId : undefined}>
      {content.title ? (
        <div className="card__head">
          <div className="card__titles">
            <h3 className="card__title" id={titleId}>
              {content.title}
            </h3>
          </div>
        </div>
      ) : null}
      <ol className="steps">
        {content.steps.map((step, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: steps in order
          <li key={i} className={`steps__item steps__item--${step.state}`}>
            <Icon name={STEP_ICON[step.state] ?? 'circle'} size={20} />
            <span className="steps__text">
              <span className="steps__label">
                {step.label}
                <span className="visually-hidden"> ({t(`chat.step.${step.state}`)})</span>
              </span>
              {step.detail ? <span className="steps__detail">{step.detail}</span> : null}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function WidgetCard({ content, open, onOpen }: { content: WidgetContent; open: boolean; onOpen: () => void }) {
  const titleId = useId();
  const { widget } = content;
  return (
    <>
      {content.text ? (
        <div className="bubble">
          <Markdown text={content.text} />
        </div>
      ) : null}
      <section className="card card--chat" aria-labelledby={titleId}>
        <div className="card__head">
          <span className={`icon-tile${widget.kind === 'upload' ? ' icon-tile--green' : ''}`}>
            <Icon name={iconFor(widget.icon) ?? (widget.kind === 'otp' ? 'lock' : 'file-text')} />
          </span>
          <div className="card__titles">
            <h3 className="card__title" id={titleId}>
              {widget.title}
            </h3>
            {widget.description ? <span className="card__subtitle">{widget.description}</span> : null}
          </div>
        </div>
        {open ? (
          <div className="card__actions">
            <Button small inline icon={widget.kind === 'otp' ? 'lock' : undefined} onClick={onOpen}>
              {widget.cta}
            </Button>
          </div>
        ) : null}
      </section>
    </>
  );
}
